import { ApiProvider, AuthClientProvider, CheckinQueueProvider } from '@church/data'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { useIsRestoring } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { indexedDbStorage } from './lib/indexeddb-storage.ts'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { api } from './lib/api.ts'
import { authClient } from './lib/auth-client.ts'
import { queryClient } from './lib/query-client.ts'
import { routeTree } from './routeTree.gen.ts'
import { followSystemTheme } from './lib/theme.ts'
import { applySavedTweaks } from './lib/design-tweaks.ts'
import { ToastProvider } from './components/Toaster.tsx'
import { RouteError, RoutePending } from './components/RouteStates.tsx'
import { keepTapsDuringTransitions, navigationTypes } from './lib/view-transition.ts'
import './styles.css'

const router = createRouter({
  routeTree,
  // Loaders prefetch through the Query client, so the router and Query share
  // one cache rather than fetching the same data twice.
  context: { queryClient },
  defaultPreload: 'intent',
  // A page that fails says why and offers Try again, inside the shell (the
  // menu stays); one that takes a moment shows rows on their way.
  defaultErrorComponent: RouteError,
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 300,
  // Another screen: a `page` view transition (lib/view-transition.ts). A
  // filter or search box changing the address doesn't animate; tabs run
  // their own (`tab`).
  defaultViewTransition: { types: navigationTypes },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

followSystemTheme()
applySavedTweaks()
keepTapsDuringTransitions()

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('index.html is missing #root')

/**
 * The query cache, kept across reloads.
 *
 * Only the kiosk needs this, and it needs it badly: the service worker gets the
 * shell up with no network, and then the app throws because it cannot ask who
 * is signed in. A persisted cache is what turns "the page loads offline" into
 * "the app runs offline".
 *
 * IndexedDB, the same store the check-in queue uses — a queue that survives a
 * reload beside a session that does not would be half a feature. Twelve hours,
 * because a Sunday morning is the unit: long enough for a kiosk left on since
 * eight, short enough that a stale identity cannot follow somebody into next
 * week.
 *
 * Nothing here is a security decision. The server re-checks every request, and
 * `ensureMe` falls back to this cache only when the transport failed — a 401
 * still signs you out.
 */
/**
 * Nothing renders until the cache is back.
 *
 * Restoration is asynchronous, and the router's `beforeLoad` runs the moment
 * it mounts — so without this gate the guard asks "who is signed in?" before
 * the persisted answer has loaded, finds nothing, and throws. Which is exactly
 * what the persistence was added to prevent, and it looks identical to the
 * bug it was meant to fix.
 *
 * The wait is milliseconds from IndexedDB; a kiosk sees a blank frame, not a
 * spinner worth designing.
 */
function AwaitRestore({ children }: { children: ReactNode }) {
  return useIsRestoring() ? null : children
}

const persister = createAsyncStoragePersister({
  storage: (() => {
    const store = indexedDbStorage('query-cache')
    return {
      getItem: () => store.read(),
      setItem: (_key: string, value: string) => store.write(value),
      removeItem: () => store.write(''),
    }
  })(),
})

/**
 * The kiosk's offline shell.
 *
 * Production only: in dev, Vite serves each module separately and caching them
 * would fight HMR. Registration is fire-and-forget — a browser that refuses
 * (private mode, an old iOS) still gets a working online app, and the queue is
 * durable either way.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  globalThis.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined)
  })
}

createRoot(rootElement).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: 12 * 60 * 60 * 1000 }}
    >
      <ApiProvider client={api}>
        <AuthClientProvider client={authClient}>
          {/* IndexedDB-backed, because a check-in kiosk runs unattended and a
              reload must not lose the children already through the door. */}
          <CheckinQueueProvider storage={indexedDbStorage('checkin')}>
            <ToastProvider>
              <AwaitRestore>
                <RouterProvider router={router} />
              </AwaitRestore>
            </ToastProvider>
          </CheckinQueueProvider>
        </AuthClientProvider>
      </ApiProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
)
